import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';
import Alert, { AlertOptions } from '../components/admin/Alert';
import Toast, { ToastOptions, ToastType } from '../components/admin/Toast';

interface AlertContextType {
  showAlert: (options: AlertOptions) => Promise<boolean | string | null>;
  showSuccess: (message: string, title?: string) => Promise<void>;
  showError: (message: string, title?: string) => Promise<void>;
  showWarning: (message: string, title?: string) => Promise<void>;
  showInfo: (message: string, title?: string) => Promise<void>;
  showConfirm: (message: string, title?: string) => Promise<boolean>;
  showPrompt: (message: string, title?: string, placeholder?: string, defaultValue?: string) => Promise<string | null>;
  toastSuccess: (message: string, duration?: number) => void;
  toastError: (message: string, duration?: number) => void;
  toastWarning: (message: string, duration?: number) => void;
  toastInfo: (message: string, duration?: number) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AlertOptions | null>(null);
  const resolvePromiseRef = useRef<((value: boolean | string | null) => void) | null>(null);
  const isResolvedRef = useRef<boolean>(false);
  const [toasts, setToasts] = useState<Array<ToastOptions & { id: string }>>([]);

  const showAlert = useCallback((alertOptions: AlertOptions): Promise<boolean | string | null> => {
    return new Promise((resolve) => {
      isResolvedRef.current = false;

      const safeResolve = (value: boolean | string | null) => {
        if (isResolvedRef.current) {
          return;
        }
        isResolvedRef.current = true;

        resolve(value);
        resolvePromiseRef.current = null;
        setTimeout(() => {
          setOptions(null);
        }, 300);
      };

      resolvePromiseRef.current = safeResolve;

      setOptions({
        ...alertOptions,
        onConfirm: (value?: string) => {
          if (alertOptions.onConfirm) {
            alertOptions.onConfirm(value);
          }
          setIsOpen(false);
          setTimeout(() => {
            const result = alertOptions.showInput ? (value || '') : true;
            safeResolve(result);
          }, 300);
        },
        onCancel: () => {
          if (alertOptions.onCancel) {
            alertOptions.onCancel();
          }
          setIsOpen(false);
          setTimeout(() => {
            const result = alertOptions.showInput ? null : false;
            safeResolve(result);
          }, 300);
        },
      });

      setIsOpen(true);
    });
  }, []);

  const handleClose = () => {

    setIsOpen(false);
    // Don't auto-resolve for confirm dialogs - let onConfirm/onCancel handle it
    // Only auto-resolve for simple alerts without cancel button
    if (resolvePromiseRef.current && options && !isResolvedRef.current) {
      if (options.type !== 'confirm' && !options.showCancel) {

        if (options.showInput) {
          resolvePromiseRef.current(null);
        } else {
          resolvePromiseRef.current(false);
        }
        resolvePromiseRef.current = null;
      } else if (options.type === 'confirm') {

      }
    }
    setTimeout(() => {
      setOptions(null);
    }, 300);
  };

  const showSuccess = useCallback((message: string, title?: string): Promise<void> => {
    return new Promise((resolve) => {
      showAlert({
        message,
        title,
        type: 'success',
        onConfirm: () => resolve(),
      });
    });
  }, [showAlert]);

  const showError = useCallback((message: string, title?: string): Promise<void> => {
    return new Promise((resolve) => {
      showAlert({
        message,
        title,
        type: 'error',
        onConfirm: () => resolve(),
      });
    });
  }, [showAlert]);

  const showWarning = useCallback((message: string, title?: string): Promise<void> => {
    return new Promise((resolve) => {
      showAlert({
        message,
        title,
        type: 'warning',
        onConfirm: () => resolve(),
      });
    });
  }, [showAlert]);

  const showInfo = useCallback((message: string, title?: string): Promise<void> => {
    return new Promise((resolve) => {
      showAlert({
        message,
        title,
        type: 'info',
        onConfirm: () => resolve(),
      });
    });
  }, [showAlert]);

  const showConfirm = useCallback((message: string, title?: string): Promise<boolean> => {
    return showAlert({
      message,
      title,
      type: 'confirm',
      showCancel: true,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
    }) as Promise<boolean>;
  }, [showAlert]);

  const showPrompt = useCallback(
    (message: string, title?: string, placeholder?: string, defaultValue?: string): Promise<string | null> => {
      return showAlert({
        message,
        title: title || 'Input Required',
        type: 'info',
        showCancel: true,
        showInput: true,
        inputPlaceholder: placeholder,
        inputValue: defaultValue,
        confirmText: 'OK',
        cancelText: 'Cancel',
      }) as Promise<string | null>;
    },
    [showAlert]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType, duration: number = 3000) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type, duration }]);

    setTimeout(() => {
      removeToast(id);
    }, duration);
  }, [removeToast]);

  const toastSuccess = useCallback((message: string, duration?: number) => {
    showToast(message, 'success', duration);
  }, [showToast]);

  const toastError = useCallback((message: string, duration?: number) => {
    showToast(message, 'error', duration);
  }, [showToast]);

  const toastWarning = useCallback((message: string, duration?: number) => {
    showToast(message, 'warning', duration);
  }, [showToast]);

  const toastInfo = useCallback((message: string, duration?: number) => {
    showToast(message, 'info', duration);
  }, [showToast]);

  return (
    <AlertContext.Provider
      value={{
        showAlert,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showConfirm,
        showPrompt,
        toastSuccess,
        toastError,
        toastWarning,
        toastInfo,
      }}
    >
      {children}
      <Alert isOpen={isOpen} options={options} onClose={handleClose} />
      <Toast toasts={toasts} onRemove={removeToast} />
    </AlertContext.Provider>
  );
};

